import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { OrderBumpService } from '../order-bump.service';
import { OrderformFascade } from '../../../fascades/orderform.fascade';
import { FormControl, FormGroup } from '@angular/forms';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Observable,
  Subscription,
} from 'rxjs';
import { QuantityInputComponent } from '../../product-list/quantity-input/quantity-input.component';
import { ProductListFascade } from '../../../fascades/product-list.fascade';
import { OrderformOrderProductItem } from '@orderform/widgets';

export const ORDER_BUMP_ARROW_ASSET_PATH =
  '/webinc/images/orderform/order_bump/';
export const ORDER_BUMP_PLACEHOLDER_ARROW_MAP = {
  RED_ARROW: 'red_arrow_right.png',
  RED_BLINK: 'red_arrow_right_blink.gif',
  RED_ARROW_LEFT: 'red_arrow_left.png',
  RED_BLINK_LEFT: 'red_arrow_left_blink.gif',
  GREEN_ARROW: 'green_arrow_right.png',
  GREEN_BLINK: 'green_arrow_right_blink.gif',
  GREEN_ARROW_LEFT: 'green_arrow_left.png',
  GREEN_BLINK_LEFT: 'green_arrow_left_blink.gif',
  BLUE_ARROW: 'blue_arrow_right.png',
  BLUE_BLINK: 'blue_arrow_right_blink.gif',
  BLUE_ARROW_LEFT: 'blue_arrow_left.png',
  BLUE_BLINK_LEFT: 'blue_arrow_left_blink.gif',
  ORANGE_ARROW: 'orange_arrow_left.png',
  ORANGE_BLINK: 'orange_arrow_left_blink.gif',
  ORANGE_ARROW_LEFT: 'orange_arrow_right.png',
  ORANGE_BLINK_LEFT: 'orange_arrow_right_blink.gif',
};

@Component({
  selector: 'orderform-placeholder',
  templateUrl: './placeholder.component.html',
  styleUrls: ['./placeholder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // ShadowDom wird hier zwingen benötigt, sonst kann man die Platzhalter im tinymce nicht Stylen!
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class PlaceholderComponent implements OnInit, OnDestroy {
  private _productIsRemoved = false;

  private _productId$ = new BehaviorSubject<string>(null);

  @Input() field: string;
  public product$ = this._productId$.pipe(
    filter((productId) => !!productId),
    switchMap((id) => this._orderBumpService.getProductById(id)),
    filter((product) => !!product),
    shareReplay(1)
  );
  public form: FormGroup<{
    product_id: FormControl<number>;
    quantity: FormControl<number>;
  }>;
  public imageUrl$: Observable<string>;
  @Input() fontSize = '1';

  private _subscriptions: Subscription[] = [];

  mainProduct$ = this._orderformFascade.mainProduct$;

  @ViewChild(QuantityInputComponent) input: QuantityInputComponent;

  constructor(
    private _orderBumpService: OrderBumpService,
    private _orderformFascade: OrderformFascade,
    private _productListFascade: ProductListFascade,
    private _el: ElementRef
  ) {
    const closestProductId = this._el.nativeElement
      .closest('.placeholder-container')
      ?.getAttribute('product-id');

    if (closestProductId) {
      this.removePreviewsProductId();
      this._productId$.next(closestProductId);
    }

    this._subscriptions.push(
      combineLatest([
        this.mainProduct$,
        this._productId$.pipe(distinctUntilChanged()),
      ]).subscribe(([mainProduct, bumpProductId]) => {
        if (+bumpProductId && +mainProduct.product_id !== +bumpProductId) {
          this._orderBumpService.addProductId(bumpProductId);
          this._productIsRemoved = false;
        }
      })
    );
  }

  ngOnInit(): void {
    const isBlink = this.field.indexOf('BLINK') >= 0;
    const isArrow = this.field.indexOf('ARROW') >= 0;

    if (this.field === 'INPUT') {
      this.form = new FormGroup({
        quantity: new FormControl<number>(0, { nonNullable: true }),
        product_id: new FormControl<number>(0, { nonNullable: true }),
      });

      this._subscriptions.push(
        this.product$.subscribe((product) => {
          this.form.patchValue(
            {
              product_id: product.product_id,
              quantity: product.orderItem.quantity?.value || 0,
            },
            { emitEvent: false }
          );
        })
      );

      this._subscriptions.push(
        this.form.valueChanges
          .pipe(withLatestFrom(this._productListFascade.cartProductList$))
          .subscribe(([values, cartProductList]) => {
            this._productListFascade.updateQuantity(
              values.product_id,
              values.quantity
            );
          })
      );
    } else if (isBlink || isArrow) {
      this.imageUrl$ = this.product$.pipe(
        map(
          (productItem) =>
            ORDER_BUMP_ARROW_ASSET_PATH +
            ORDER_BUMP_PLACEHOLDER_ARROW_MAP[
              isBlink && productItem?.orderItem.quantity.value > 0
                ? this.field.replace('_BLINK', '_ARROW')
                : this.field
            ]
        )
      );
    }
  }

  /**
   * Remove set ProductId from Orderbump ProductIds Lists
   */
  removePreviewsProductId() {
    if (this._productIsRemoved === false && this._productId$.value) {
      this._orderBumpService.removeProductId(this._productId$.value);
      this._productIsRemoved = true;
    }
  }

  ngOnDestroy() {
    // Cleanup orderbump productId list if productId changes
    this.removePreviewsProductId();

    this._subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Diese Listener werden benötigt, damit Kind Elemente
   * beim Modus ShadowDom die Events auch empfangen können!
   * @param e
   */

  /* istanbul ignore next */
  @HostListener('mouseleave', ['$event'])
  onMouseLeave(e: MouseEvent) {}

  /* istanbul ignore next */
  @HostListener('mouseenter', ['$event'])
  onMouseEnter(e: MouseEvent) {}

  /* istanbul ignore next */
  @HostListener('focus', ['$event'])
  onFocus(e: FocusEvent) {}

  /* istanbul ignore next */
  @HostListener('blur', ['$event'])
  onBlur(e: FocusEvent) {}
}
